import classnames from "classnames";
import React from "react";
import CountUp from "react-countup";
import { Card, CardBody, CardTitle, Col, Row } from "reactstrap";

/**
 * A stats card that displays a number with proper formatting.
 * Based on @general-backoffice/core NumberCard but with better number formatting.
 */
interface NumberStatsCardProps {
  color?: string;
  title: React.ReactNode;
  number: number;
  displayValue?: React.ReactNode;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  description?: React.ReactNode;
  icon?: string;
  isDark?: boolean;
  isLoading?: boolean;
}

const NumberStatsCard = ({
  color = "default",
  title,
  number,
  displayValue,
  prefix = "",
  suffix = "",
  decimals = 2,
  description,
  icon = "fas fa-chart-line",
  isDark = true,
  isLoading = false,
}: NumberStatsCardProps) => {
  const bgGradient = `bg-gradient-${color}`;

  return (
    <Card className={classnames("w-100", { [bgGradient]: isDark, "bg-white": !isDark })}>
      <CardBody>
        <Row>
          <div className="col">
            <CardTitle
              tag="h5"
              className={classnames("text-uppercase text-muted mb-0", {
                "text-white": isDark,
              })}
            >
              {title}
            </CardTitle>
            <span
              className={classnames("h2 font-weight-bold mb-0", {
                "text-white": isDark,
              })}
            >
              {isLoading ? (
                <span className="text-muted">...</span>
              ) : displayValue !== undefined ? displayValue : (
                <>
                  {prefix}
                  <CountUp end={number} preserveValue={true} decimals={decimals} decimal="," separator="." />
                  {suffix}
                </>
              )}
            </span>
          </div>
          <Col className="col-auto">
            <div
              className={classnames("icon icon-shape rounded-circle shadow", {
                [bgGradient]: !isDark,
                "bg-white": isDark,
                "text-white": !isDark,
              })}
            >
              <i className={icon} />
            </div>
          </Col>
        </Row>
        <p className={classnames("mt-3 mb-0 text-sm", { "text-white": isDark })}>{description}</p>
      </CardBody>
    </Card>
  );
};

export default NumberStatsCard;
